export function describeCurrentPage() {
  const path = window.location.pathname
  const params = new URLSearchParams(window.location.search)

  const propertyMatch = path.match(/^\/properties\/([a-z0-9-]+)\/?$/i)
  if (propertyMatch && !['chat', 'search', 'admin'].includes(propertyMatch[1])) {
    const heading = document.querySelector('h1')
    const name = heading?.textContent?.trim() ||
      propertyMatch[1].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    const result = { type: 'property', name, slug: propertyMatch[1] }

    // A real quote exists only when the widget shows a fetched quote or the
    // URL carries a quote_id. The hidden quote_id input alone is NOT evidence:
    // every property page has one for the session's empty placeholder quote.
    const quoteSuccess = document.querySelector('.quote-success:not(.hidden)')
    const urlQuoteId = params.get('quote_id')
    if (quoteSuccess || urlQuoteId) {
      result.has_quote = true
      result.quote_id = urlQuoteId || document.querySelector("input[name='quote_id']")?.value
    }
    return result
  }

  if (path.includes('/reservations/new')) {
    const bookingSlug = path.match(/\/properties\/([a-z0-9-]+)/i)?.[1]
    const name = bookingSlug
      ? bookingSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : 'your property'
    return { type: 'booking', name, slug: bookingSlug }
  }

  if (path === '/properties' || path === '/properties/') {
    const filters = describeSearchFilters(params)
    return { type: 'search', filters }
  }

  if (path.startsWith('/properties')) {
    return { type: 'other', name: path }
  }

  return { type: 'home', name: 'Home' }
}

function describeSearchFilters(params) {
  const labels = {
    bedrooms_gteq: 'BR',
    max_occupancy_gteq: 'guests',
    check_in: 'arriving',
    check_out: 'departing',
    hot_tub: 'hot tub',
    pets_friendly_eq: 'pet-friendly',
    air_conditioning: 'A/C',
    sharc_passes_included: 'SHARC passes',
    luxury_retreat: 'luxury',
    game_table: 'game table',
    king_beds: 'king bed',
    single_floor: 'single floor',
    ev_charger: 'EV charger',
    family_loving: 'family-friendly'
  }

  const parts = []
  for (const [key, value] of params) {
    if (!key.startsWith('q[') || !value || key.includes('viewmode') || key.includes('sort')) continue
    const name = key.replace('q[', '').replace(']', '').replace('[]', '')
    const label = labels[name] || name.replace(/_/g, ' ')

    if (value === '1' || value === 'true') {
      parts.push(label)
    } else if (name === 'bedrooms_gteq') {
      parts.push(`${value}+ ${label}`)
    } else if (name === 'max_occupancy_gteq') {
      parts.push(`${value}+ ${label}`)
    } else {
      parts.push(`${label}: ${value}`)
    }
  }
  return parts.join(', ')
}

export function gatherPageContext() {
  const context = {}
  const path = window.location.pathname
  const params = new URLSearchParams(window.location.search)

  const propertyMatch = path.match(/^\/properties\/([a-z0-9-]+)\/?$/i)
  if (propertyMatch && propertyMatch[1] !== 'chat') {
    context.page = 'property'
    context.slug = propertyMatch[1]
    const heading = document.querySelector('h1')
    if (heading) context.name = heading.textContent.trim()

    // Include the page's active quote so the assistant knows its status.
    // The hidden quote_id input alone is not evidence of a real quote (it
    // exists on every property page for the session's placeholder quote).
    const quoteSuccess = document.querySelector('.quote-success:not(.hidden)')
    const urlQuoteId = params.get('quote_id')
    if (quoteSuccess || urlQuoteId) {
      context.has_quote = true
      const quoteId = urlQuoteId || document.querySelector("input[name='quote_id']")?.value
      if (quoteId) context.quote_id = quoteId
    }
  } else if (path === '/properties' || path === '/properties/') {
    context.page = 'search'
    const filters = {}
    for (const [key, value] of params) {
      if (key.startsWith('q[') && value) {
        const filterName = key.replace('q[', '').replace(']', '')
        filters[filterName] = value
      }
    }
    if (Object.keys(filters).length > 0) context.filters = filters
  } else if (path.includes('/reservations/new')) {
    context.page = 'booking'
    const bookingSlug = path.match(/\/properties\/([a-z0-9-]+)/i)?.[1]
    if (bookingSlug) context.slug = bookingSlug
    if (params.get('arrive')) context.arrive = params.get('arrive')
    if (params.get('depart')) context.depart = params.get('depart')
    if (params.get('adults')) context.adults = params.get('adults')
    const bookingQuoteId = params.get('quote_id') || document.querySelector("input[name='quote_id']")?.value
    if (bookingQuoteId) {
      context.has_quote = true
      context.quote_id = bookingQuoteId
    }
  } else if (path === '/' || path === '') {
    context.page = 'home'
  } else {
    context.page = path
  }

  const viewed = getViewedProperties()
  if (viewed.length > 0) context.viewed = viewed

  return context
}

export function buildPillLabel(pageInfo) {
  switch (pageInfo.type) {
    case 'property': return `Viewing ${pageInfo.name}`
    case 'booking': return `Booking ${pageInfo.name}`
    case 'search': return pageInfo.filters ? `Search: ${pageInfo.filters}` : 'Browsing all properties'
    default: return pageInfo.name || 'Browsing'
  }
}

export function trackCurrentPageView() {
  const match = window.location.pathname.match(/^\/properties\/([a-z0-9-]+)\/?$/i)
  if (match && match[1] !== 'chat') {
    const heading = document.querySelector('h1')
    trackPropertyView(match[1], heading?.textContent?.trim())
  }
}

function trackPropertyView(slug, name) {
  const viewed = getViewedProperties()
  const existing = viewed.findIndex(p => p.slug === slug)
  if (existing >= 0) viewed.splice(existing, 1)
  viewed.push({ slug, name: name || slug })
  while (viewed.length > 10) viewed.shift()
  sessionStorage.setItem('kailani_viewed', JSON.stringify(viewed))
}

function getViewedProperties() {
  try {
    return JSON.parse(sessionStorage.getItem('kailani_viewed') || '[]')
  } catch { return [] }
}

// Should Kailani say anything about the page the guest is on?
//
// A context note exists to explain a change of page part-way through a
// conversation: "I see you've moved to Tan Oak 29". On a chat that has only
// just opened there is nothing to add context to. The guest opened the panel
// on this page, read the greeting, and then got "Back to the full property
// list. Tell me what you're looking for", which reads as a non-sequitur and
// pushes the greeting's three questions out of view.
//
// So: only once something has actually been said.
export function shouldAnnouncePageChange({ isOpen, chatId, messageCount }) {
  if (!isOpen || !chatId) return false

  return messageCount > 0
}
